import { Component, OnDestroy, OnInit, Input, Output, EventEmitter, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { of, from, Subscription } from 'rxjs';
import { map, concatMap, filter, finalize } from 'rxjs/operators';

import { Title } from "@angular/platform-browser";

import * as XLSX from 'xlsx';

import { environment } from 'src/environments/environment';

import { Product } from 'src/app/shared/product'

import { ImportItemFields } from 'src/app/shared/document';

import { GlobalSettingsData } from 'src/app/shared/global-settings-data'
import { GetglobalsettingsService } from 'src/app/services/getglobalsettings.service'

import { ProductApiService } from 'src/app/services/product-api.service';

function ExcelDateToJSDate(serial) {
  var utc_days = Math.floor(serial - 25569);
  var utc_value = utc_days * 86400;
  var date_info = new Date(utc_value * 1000);

  var fractional_day = serial - Math.floor(serial) + 0.0000001;

  var total_seconds = Math.floor(86400 * fractional_day);

  var seconds = total_seconds % 60;

  total_seconds -= seconds;

  var hours = Math.floor(total_seconds / (60 * 60));
  var minutes = Math.floor(total_seconds / 60) % 60;

  return new Date(date_info.getFullYear(), date_info.getMonth(), date_info.getDate(), hours, minutes, seconds);
}

function parseMyFloat(txt: string): number {
  return (typeof txt === 'string') ? parseFloat(txt.replace(' ', '').replace('.', '').replace(',', '.')) : txt;
}

function parseTextBuffer(txt: string, valueSeparator: string): any {
  // Split data into lines and separate headers from actual data
  // using Array spread operator
  const [headerLine, ...lines] = txt.split('\n');

  // Split headers line into an array
  // `valueSeparator` may come from some kind of argument
  // You may want to transform header strings into something more
  // usable, like `camelCase` or `lowercase-space-to-dash`

  const headers = headerLine.split(valueSeparator);

  const objects = lines
    .map((line, index) => {
      return line.split(valueSeparator).reduce((object, value, index) => ({ ...object, [headers[index]]: value, }), {});
    });

  return objects;
}

@Component({
  selector: 'app-create-price-list',
  templateUrl: './create-price-list.component.html',
  styleUrls: ['./create-price-list.component.css'],
  encapsulation: ViewEncapsulation.None
})
/**
 * Price-list import tool: accepts an Excel file or tab/CSV text, maps columns to product
 * fields, and emits matched products via `importIsDone`.
 *
 * Three module-level helpers handle data conversion: `ExcelDateToJSDate` (serial → Date),
 * `parseMyFloat` (locale string → number), and `parseTextBuffer` (tab/CSV → row objects).
 *
 * The three-step accordion (file selector → field mapping → import result) is driven by
 * `openFileSelector`, `openFieldSelector`, and `openImportResult` flags.
 *
 * `onDataInsert` runs `from(dataSource).pipe(filter, concatMap(getProductByCode), concatMap(from),
 * filter, concatMap(push))` with a `finalize` re-spread to trigger child view updates. Column
 * names are extracted from the first data row in `processDataSource`.
 *
 * Inputs configure the search strategy (`searchOld`, `searchComplex`, `partialMatch`),
 * list-merge behaviour (`clearBefore`, `overwriteExisted`, `addExisted`), and which columns
 * appear in the result view (`showPrice`).
 */
export class CreatePriceListComponent implements OnInit, OnChanges, OnDestroy {
  private globalSettinsData: GlobalSettingsData;

  @Input() paramsMap: ImportItemFields[] = [
    { fieldName: 'PRODCODE', fieldLabel: 'Код товара', fieldIsMandatory: true, fieldType: 'str' },
    { fieldName: 'PRODNAME', fieldLabel: 'Наименование товара', fieldIsMandatory: false, fieldType: 'str' },
    { fieldName: 'PRODPRICEUAH2', fieldLabel: 'Цена товара', fieldIsMandatory: false, fieldType: 'num' },
    { fieldName: 'PRODPRICEUSD2', fieldLabel: 'Цена в валюте', fieldIsMandatory: false, fieldType: 'num' },
  ];

  @Input() clearBefore: boolean = true;
  @Input() overwriteExisted: boolean = false;
  @Input() addExisted: boolean = true;

  @Input() searchOld: boolean = false;
  @Input() searchComplex: boolean = false;
  @Input() partialMatch: boolean = true;

  @Input() showPrice: boolean = true;

  @Output() importIsDone = new EventEmitter<Product[]>();     //

  blocked: boolean = false;
  loading: boolean = false;

  tabIndex: number = 1;     // Insert from text buffer by default

  private _dataSourceQuery: Subscription;

  /** `true` when the product list is empty or a load is in progress. */
  public get notReady(): boolean { return ((this.products.length == 0) || this.loading); }

  textSource: string;

  dataSource: any[];
  columns: string[] = [];

  products: Product[] = [];

  textType: string = 'tab';   // Either 'csv' so far
  textDelimiter: string = '\t';

  /** `true` when `col` is already mapped to a `paramsMap` field. */
  public columnMet(col: string): boolean { return (this.paramsMap.find(e => (e.importColumnName || '') == col)) ? true : false; }

  /** `true` when all mandatory `paramsMap` fields have a mapped column. */
  public get canInsertData(): boolean {
    let canInsert = true;

    this.paramsMap
      .filter(e => e.fieldIsMandatory)
      .forEach(e => canInsert = canInsert && this.columns.includes(e.importColumnName || ''))
    return canInsert;
  };

  /** `true` when the text source area is non-empty. */
  public get canInsertTextData(): boolean { return (this.textSource || '').trim() != ''; };

  openFileSelector: boolean = true;
  openFieldSelector: boolean = false;
  openImportResult: boolean = false;

  constructor(
    private prodApi: ProductApiService,
    private globalsettingsservice: GetglobalsettingsService,
    private titleService: Title,
  ) { }

  /** Logs `paramsMap` changes; reserved for future per-input logic. */
  ngOnChanges(changes: SimpleChanges) {

    for (let propName in changes) {
      let change = changes[propName];

      console.log('*** CreatePriceListComponent ngOnChanges ' + propName + ' (actual): ' + JSON.stringify(change.currentValue));
      //console.log('*** CreateNewDocumentComponent ngOnChanges ' + propName + ' (was):    ' + JSON.stringify(change.previousValue));

      switch (propName) {

        case 'paramsMap':
          break;
      }
    }
  }

  /** Clears in-memory buffers and unsubscribes the active import query. */
  ngOnDestroy(): void {
    console.log('*** CreatePriceListComponent in ngOnDestroy')

    this.products = [];
    this.columns = [];
    this.textSource = '';

    if (this._dataSourceQuery) { this._dataSourceQuery.unsubscribe(); }
  }

  /** Reads global settings and sets the browser title. */
  ngOnInit(): void {
    console.log('*** CreatePriceListComponent in ngOnInit');

    this.globalSettinsData = this.globalsettingsservice.getGlobalSettings();
    this.titleService.setTitle('Создание прайс-листов');
  }

  /** Starts the sequential product lookup pipeline and populates `products`. */
  onDataInsert(event) {
    console.log('*** CreatePriceListComponent onDataInsert: ' + JSON.stringify(event));

    this.openFieldSelector = false;
    this.openImportResult = true;

    if (this.canInsertData) {
      if (this.clearBefore) {
        this.products = [];
      }

      let prodCodeColumnName = (this.paramsMap.find(e => (e.fieldName) == 'PRODCODE') || {}).importColumnName || '';
      let diPriceColumnName = (this.paramsMap.find(e => (e.fieldName) == 'PRODPRICEUAH2') || {}).importColumnName || '';
      let diVPriceColumnName = (this.paramsMap.find(e => (e.fieldName) == 'PRODPRICEUSD2') || {}).importColumnName || '';

      this.loading = true;

      this._dataSourceQuery =
        from(this.dataSource).pipe(

          filter(row => { environment.production || console.log(row[prodCodeColumnName]); return (row[prodCodeColumnName] || '').length > 3; }),

          concatMap(row => this.prodApi.getProductByCode(row[prodCodeColumnName], this.searchOld, this.searchComplex, this.partialMatch).pipe(

            concatMap(prs => from(prs)),

            filter(product => ((product != null) && (product != undefined))),

            concatMap(product => of(this.products.push(product))),

          )),

          finalize(() => {
            this.loading = false;

            this.products = [... this.products];  // Trigger updates in child view

            environment.production || console.table(this.products);
          })
          )
          .subscribe();
    }
  }

  /** Upload progress handler (diagnostic only). */
  onProgress(event) {
    console.log('*** CreatePriceListComponent onProgress: ' + JSON.stringify(event));

  }

  /** Extracts column names from `dataSource[0]` and advances to the field-mapping step. */
  processDataSource() {
    this.columns = [];

    console.log('*** CreatePriceListComponent reader.sheet_to_json: ' + JSON.stringify(this.dataSource));
    if ((this.dataSource || []).length > 0) {

      console.log('*** CreatePriceListComponent reader.sheet_to_json[0]: ' + JSON.stringify(this.dataSource[0]));
      for (const keys in this.dataSource[0]) {
        console.log('*** CreatePriceListComponent reader.data.keys: ' + keys);

        this.columns.push(keys);
      }

      if (this.columns.length == 1) {

        this.paramsMap[0].importColumnName = this.columns[0];

      }

      this.openFileSelector = false;
      this.openFieldSelector = true;
    } else {
      console.log('*** CreatePriceListComponent reader.sheet_to_json: SEEMS EMPTY');

    }
  }

  /** Accordion tab-change handler (diagnostic only). */
  onTabChange(event) {
    console.log('*** CreatePriceListComponent onProgress: ' + JSON.stringify(event));

  }

  /** Parses `textSource` via `parseTextBuffer` and advances to field mapping. */
  onInsertText(event) {
    console.log('*** CreatePriceListComponent onInsertText: ');

    this.dataSource = parseTextBuffer(this.textSource, this.textDelimiter);

    //    this.dataSource = txtToJSON({ data: this.textSource });
    console.log('*** CreatePriceListComponent reader.onload: ' + JSON.stringify(this.dataSource))
    this.processDataSource();
  }

  /** Reads the selected file with `FileReader`, parses it as xlsx, and advances to field mapping. */
  onFileChange(event) {
    console.log('*** CreatePriceListComponent onBasicUpload: ');

    const target: DataTransfer = <DataTransfer>(event.target);

    this.dataSource = [];

    if (target.files.length !== 1) throw new Error('Cannot use multiple files');

    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      console.log('*** CreatePriceListComponent reader.onload: ' + wsname)

      this.dataSource = [...XLSX.utils.sheet_to_json(ws)];

      this.processDataSource();

      /* save data */
      // this.data = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 }));
      // console.log(this.data);
    };
    reader.readAsBinaryString(target.files[0]);
  }

  /** PrimeNG upload handler — reads the uploaded file and parses it as xlsx. */
  onBasicUpload(event) {
    console.log('*** CreatePriceListComponent onBasicUpload: ' + JSON.stringify(event));

    const target: DataTransfer = <DataTransfer>(event.files);

    if (target.files.length !== 1) throw new Error('Cannot use multiple files');
    const reader: FileReader = new FileReader();
    reader.onload = (e: any) => {
      /* read workbook */
      const bstr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });

      /* grab first sheet */
      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      console.log('*** CreatePriceListComponent reader.onload: ' + wsname)

      /* save data */
      // this.data = <AOA>(XLSX.utils.sheet_to_json(ws, { header: 1 }));
      // console.log(this.data);
    };
    reader.readAsBinaryString(target.files[0]);
  }

  /** Updates `textDelimiter` based on the selected text-type radio and clears the data buffer. */
  onTextTypeClick(event) {
    console.log('*** CreatePriceListComponent onTextTypeClick: ' + JSON.stringify(event));

    switch (this.textType) {
      case 'tab': this.textDelimiter = '\t'; break;
      case 'csv': this.textDelimiter = ';'; break;
      default: this.textDelimiter = '\t'; break;
    }
    this.columns = [];
    this.dataSource = [];
  }
}
